import BLTService from '@/core/apis/blt_api';
import { TDestinationSetting } from '@/core/apis/config_api/config_api_types';
import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { DestinationReqCoreEvent } from '@/core/constants/helpers_types/types';
import { BrowserInstanceData } from '@/core/data_handlers/browser_instance';
import { ConsentHandler } from '@/core/data_handlers/consent';
import { EConsentValue } from '@/core/data_handlers/consent/types';
import { ShopConfigHandler } from '@/core/data_handlers/shop_config_data/config_handler';
import { ServiceMetricsUtils } from '@/core/event_logic/utils/service_metrics_utils';
import { EDestinationType } from '@/core/interfaces';
import { Logger } from '@/shared/logger/logger';

import { DestinationBundleBuilder } from './bundle_builder';
import { DestinationBundle } from './bundles/abstract';
import { DestinationReqManger } from './destination_req_manager';

export class Destinations {
  private static instance: Destinations;
  private initializedDestinations: DestinationBundle[] = [];

  private constructor() {}

  public static getInstance(): Destinations {
    if (!Destinations.instance) {
      Destinations.instance = new Destinations();
    }
    return Destinations.instance;
  }

  public async init(
    destinationConfigs: TDestinationSetting[],
    shopConfig: ShopConfigHandler,
    bid: BrowserInstanceData,
  ): Promise<boolean> {
    let maybeDestinations: any[] = await Promise.all(
      destinationConfigs
        .filter(
          dest =>
            dest.active &&
            !Destinations.shouldFilterDestinationForConsent(
              dest.type,
              shopConfig,
            ),
        )
        .map(async dest => {
          const bundle = DestinationBundleBuilder.build(
            dest.type,
            dest.id,
            dest.creds,
          );
          if (bundle) {
            void bundle.init(bid);
            return bundle;
          }
          return;
        }),
    );
    maybeDestinations = maybeDestinations.filter(
      (bundle: any) => bundle !== undefined,
    );

    this.initializedDestinations = maybeDestinations;

    if (typeof window !== 'undefined') {
      (window as any).__INITIALIZED_DESTINATIONS__ =
        this.initializedDestinations;
    }

    return this.initializedDestinations.length > 0;
  }

  public getSupportedDestinationBundles(
    event: TMableEvent,
  ): DestinationBundle[] {
    return this.initializedDestinations
      .filter(bundle => bundle.isEventAllowedForDestination(event?.en))
      .filter(
        bundle =>
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          bundle.isMarketAllowedForDestination(event?.sd?.mid?.mid), // Market will always be provided for events
      );
  }

  public static createDestinationRequestEventBase(
    mableEvent?: TMableEvent,
  ): DestinationReqCoreEvent {
    return mableEvent as unknown as DestinationReqCoreEvent;
  }

  public static async processEvent(
    mableEvent: TMableEvent,
    shopConfig: ShopConfigHandler,
  ): Promise<boolean> {
    if (Object.keys(mableEvent).length === 0) return false;

    // Filter  out specific events and Custom Event with cen=Custom Event
    const filteredEvents = new Set([
      'search_submitted',
      'login',
      'register',
      'CART_VIEWED',
      'product_removed_from_cart',
    ]);
    if (
      filteredEvents.has(mableEvent.en) ||
      filteredEvents.has(mableEvent.esd?.cen || '') ||
      (mableEvent.en === EMableEventName.CUSTOM_EVENT &&
        mableEvent.esd?.cen === 'Custom Event')
    ) {
      return false;
    }

    let mableEvents = [mableEvent];
    const instance = Destinations.getInstance();

    const eventName = mableEvent.en;
    const destinationBundles =
      instance.getSupportedDestinationBundles(mableEvent);
    if (!mableEvent.md) {
      Logger.error(`Event ${mableEvent.en} has no metadata. Skipping event.`);
      return false;
    }
    mableEvent.md.duids = destinationBundles
      .map(bundle => bundle.destinationId)
      .filter(
        (id): id is string =>
          typeof id === 'string' &&
          id !== '0' &&
          /^[\da-f]{8}-[\da-f]{4}-[1-5][\da-f]{3}-[89ab][\da-f]{3}-[\da-f]{12}$/i.test(
            id,
          ),
      );

    if (eventName === EMableEventName.ORDER_COMPLETED) {
      mableEvent.esd?.tags?.push(
        ...Destinations.calculateBrowserDestinationSendingStatusTags(
          destinationBundles,
        ),
      );

      // Split destinations into Mable and non-Mable
      const mableDestinations = destinationBundles.filter(
        bundle => bundle.type === EDestinationType.Mable,
      );
      const nonMableDestinations = destinationBundles.filter(
        bundle => bundle.type !== EDestinationType.Mable,
      );

      // Process in parallel:
      // 1. Send original event to Mable destinations
      // 2. Calculate business logic
      const [mableEventsFromBL] = await Promise.all([
        Destinations.calculateBusinessLogic(mableEvent),
        ...mableDestinations.map(bundle =>
          Destinations.processDestinationBundle(
            bundle,
            mableEvent,
            shopConfig,
            mableEvent.en,
          ),
        ),
      ]);

      // Set mableEvents to result of business logic calculation
      mableEvents = mableEventsFromBL;

      // Process non-Mable destinations with all events from business logic
      const processingPromises = nonMableDestinations.flatMap(bundle =>
        mableEvents.map(event =>
          Destinations.processDestinationBundle(
            bundle,
            event,
            shopConfig,
            event.en,
          ),
        ),
      );

      await Promise.all(processingPromises);
    } else {
      // For non-ORDER_COMPLETED events, use the original logic
      const processingPromises = destinationBundles.flatMap(bundle =>
        mableEvents.map(event =>
          Destinations.processDestinationBundle(
            bundle,
            event,
            shopConfig,
            event.en,
          ),
        ),
      );

      await Promise.all(processingPromises);
    }

    return true;
  }

  private static async processDestinationBundle(
    bundle: DestinationBundle,
    mableEvent: TMableEvent,
    shopConfig: ShopConfigHandler,
    eventName: EMableEventName,
  ): Promise<void> {
    // Add service metrics for the destination only once when entering destination processing
    // Only add metrics for Mable destination, which we use to measure total destination processing time
    if (bundle.type === EDestinationType.Mable) {
      mableEvent = await ServiceMetricsUtils.addServiceMetric(
        mableEvent,
        'mable_destination',
      );
    }

    const { consentAllowsSending, destinationSupportsEvent, shouldSend } =
      Destinations.calculateSendingConditions(
        bundle,
        eventName,
        shopConfig,
        mableEvent,
      );

    if (shouldSend) {
      // Don't update exit time here - it will be updated in mable_destination.ts
      await Destinations.sendEventsToDestination(
        bundle,
        [mableEvent],
        eventName,
        bundle.type,
        shopConfig,
      );
    } else {
      Logger.info(
        `Event ${eventName} not sent to destination ${bundle.type}. Consent: ${consentAllowsSending}, Supported: ${destinationSupportsEvent}`,
      );
    }
  }

  private static readonly filterTags: string[] = [
    'ado_nc_new_cust_filter',
    'ado_nc_ret_cust_filter',
    'ado_nc_all_cust_filter',
    'dh_rec_sub_filter',
    'dh_zero_cv_filter',
  ];

  private static shouldFilterDestinationForConsent(
    destinationType: EDestinationType,
    shopConfig: ShopConfigHandler,
  ): boolean {
    const shouldFilter =
      shopConfig.isPerDestinationConsentActive() &&
      ConsentHandler.getConsentForDestination(destinationType) !==
        EConsentValue.ACCEPTED;
    if (shouldFilter)
      Logger.info(`Destination ${destinationType} filtered: ${shouldFilter}`);
    return shouldFilter;
  }

  static calculateSendingConditions(
    destinationBundle: DestinationBundle,
    eventName: string,
    shopConfig: ShopConfigHandler,
    mableEvent: TMableEvent,
  ): Record<string, boolean> {
    const eventTags = mableEvent.esd?.tags || [];
    const isBltEvent = eventTags.includes('blt_event');
    const isNonBltEvent = eventTags.includes('non_blt_event');
    const isMableDestination =
      destinationBundle.type === EDestinationType.Mable;

    const filterMap: {
      bltAllowsSending: boolean;
      consentAllowsSending: boolean;
      destinationSupportsEvent: boolean;
    } = {
      // BLT sending logic:
      // - If event has no BLT tags: send to all destinations
      // - If event is BLT: send to all except Mable
      // - If event is non-BLT: send only to Mable
      bltAllowsSending:
        (!isBltEvent && !isNonBltEvent) || // No BLT tags - send to all
        (isBltEvent &&
          !isMableDestination &&
          !this.filterTags.some(tag => eventTags.includes(tag))) || // BLT event - send to non-Mable
        (isNonBltEvent && isMableDestination), // Non-BLT event - send to Mable only

      consentAllowsSending: !Destinations.shouldFilterDestinationForConsent(
        destinationBundle.type,
        shopConfig,
      ),
      destinationSupportsEvent: destinationBundle.isEventAllowedForDestination(
        eventName as EMableEventName,
      ),
    };

    return {
      ...filterMap,
      shouldSend:
        filterMap.consentAllowsSending &&
        filterMap.destinationSupportsEvent &&
        filterMap.bltAllowsSending,
    };
  }

  private static async sendEventsToDestination(
    destinationBundle: DestinationBundle,
    mableEvents: TMableEvent[],
    eventName: EMableEventName,
    destinationType: EDestinationType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _shopConfig: ShopConfigHandler,
  ) {
    await Promise.all(
      mableEvents.map(async evt => {
        // eslint-disable-next-line @typescript-eslint/await-thenable
        const destinationPayload =
          // eslint-disable-next-line @typescript-eslint/await-thenable
          await destinationBundle.calculatePayload(evt);
        const {
          destinationReqEvent,
          reason: errorReason,
          success,
        } = await destinationBundle.sendToDestination(destinationPayload);
        if (success) {
          Logger.info(
            `Destination ${destinationType} sent event ${eventName} (EID = ${mableEvents[0]?.eid}). Success: ${success}, Reason: ${errorReason}`,
          );
        } else {
          // if the err reason contains No conversion ID then only log a warning
          if (errorReason?.includes('No conversion ID')) {
            Logger.warn(
              `Destination ${destinationType} failed to send event ${eventName} (EID = ${mableEvents[0]?.eid}). Success: ${success}, Reason: ${errorReason}`,
            );
          } else {
            Logger.error(
              `Destination ${destinationType} failed to send event ${eventName} (EID = ${mableEvents[0]?.eid}). Success: ${success}, Reason: ${errorReason}`,
            );
          }
        }

        if (destinationBundle.isEventAllowedForDestination(eventName)) {
          await DestinationReqManger.sendDestReqPayloads(destinationReqEvent);
        }
      }),
    );
  }

  private static async calculateBusinessLogic(
    mableEvent: TMableEvent,
  ): Promise<TMableEvent[]> {
    const preBltEvents: TMableEvent[] = [mableEvent];

    try {
      const { events } = await BLTService.processEvent(mableEvent);
      if (events) {
        events.forEach(event => {
          // Ensure esd and tags are initialized
          if (!event.esd) {
            event.esd = {};
          }
          if (!event.esd.tags) {
            event.esd.tags = [];
          }
          event.esd.tags.push('blt_event'); // we only want to send blt modified events to browser destinations
        });

        // Ensure esd and tags are initialized for mableEvent
        if (!mableEvent.esd) {
          mableEvent.esd = {};
        }
        if (!mableEvent.esd.tags) {
          mableEvent.esd.tags = [];
        }
        mableEvent.esd.tags.push('non_blt_event'); // we only want to send non-blt events to seo to calculate their values in the blt server pipeline

        return [mableEvent].concat(events);
      }
      return preBltEvents;
    } catch (error: any) {
      Logger.error(
        `Error applying business logic to event ${mableEvent.en}: ${error}`,
      );
    }

    return preBltEvents;
  }

  private static calculateBrowserDestinationSendingStatusTags(
    destinationBundles: DestinationBundle[],
  ): string[] {
    return destinationBundles
      .filter(bundle => bundle.hasBrowserDestination)
      .map(
        bundle =>
          `${bundle.type}_${bundle.destinationId}_browser_sending_${bundle.getBrowserDestinationSendingStatus() === undefined ? 'error' : bundle.getBrowserDestinationSendingStatus() ? 'success' : 'blocked'}`,
      );
  }
}

export async function initializeDestinations(
  destinationConfigs: TDestinationSetting[],
  shopConfig: ShopConfigHandler,
  bid: BrowserInstanceData,
) {
  await Destinations.getInstance().init(destinationConfigs, shopConfig, bid);
}

// export function getInitializedDestinations(): DestinationBundle[] {
//   return typeof window === 'undefined'
//     ? []
//     : (window as any).__INITIALIZED_DESTINATIONS__ || [];
// }
